/**
 * @copyright (C) 2024, rsupport. All rights reserved
 *
 * @license
 * This software and/or source code may be used, copied and/or disseminated only
 * with the written permission of rsupport, or in accordance with the terms
 * and conditions stipulated in the agreement/contract under which the software
 * and/or source code has been supplied by rsupport or its affiliates.
 * Unauthorized use, copying, or dissemination of this file, via any medium, is
 * strictly prohibited, and will constitute an infringement of copyright.
 *
 * @author
 * Heeseok Shin (hsshin@rsupport.com)
 *
 * @description
 *
 * @created at Wed Apr 24 2024
 **/

import { _T } from "@rfice/language";
import { Extension } from "@/frameworks/designStuff/components";
import { RegExp } from "@/frameworks/core/commons";

/**
사용자의 위치정보와 상태를 종합하여 현재 위치 상태를 반환합니다.
*/
const checkSpaceLocationState = ({
	currentSpace,
	currentConference,
	activeStatus
}: {
	currentSpace?: SwaggerModels.CurrentUserSpaceResponseVO;
	currentConference?: SwaggerModels.CurrentConferenceResponseVO;
	activeStatus?: SwaggerModels.UserActiveStatus;
}): "ONLINE" | "OFFLINE" | string => {
	if (!activeStatus || activeStatus === "OFFLINE") return "OFFLINE";
	const _checkPriorityCurrentSpaceType = checkPriorityCurrentSpaceType({ currentSpace, currentConference });
	switch (_checkPriorityCurrentSpaceType) {
		case "SPACE": {
			if (currentSpace?.spaceType === "MY_ROOM") {
				return _T("마이룸");
			} else {
				return currentSpace?.spaceName || "";
			}
		}
		case "CONFERENCE": {
			if (currentConference?.privateMeeting) {
				return _T("비공개");
			} else {
				return currentConference?.title || "";
			}
		}
		default: {
			return "ONLINE";
		}
	}
};

/**
 * @description
 * currentSpace와 currentConference 의 participatedTime 을 비교하여 미니프로필에 참여중 스페이스로 노출되고,
 * 따라가기 허용여부 검증의 경우에도 currentSpace와 currentConference 모두 null이 아닌 경우 participatedTime 비교하여 더 최근의 것을 대상으로 currentSpace 인 경우
 * /users/{userId}/follow_check/space/{spaceId} 를, currentConference 인 경우 /users/{userId}/follow_check/conference/{conferenceId} 를 호출해주시면 됩니다.
 *
 */
export const checkPriorityCurrentSpaceType = ({
	currentSpace,
	currentConference
}: {
	currentSpace?: SwaggerModels.CurrentUserSpaceResponseVO;
	currentConference?: SwaggerModels.CurrentConferenceResponseVO;
}): "SPACE" | "CONFERENCE" | "ONLINE" => {
	if (currentSpace?.participatedTime && currentConference?.participatedTime) {
		return currentSpace.participatedTime > currentConference.participatedTime * 1000 ? "SPACE" : "CONFERENCE";
	}
	if (currentConference) return "CONFERENCE";
	if (currentSpace) return "SPACE";
	return "ONLINE";
};

export const i18nSpaceLocationName = ({
	currentSpace,
	currentConference,
	activeStatus
}: Parameters<typeof checkSpaceLocationState>[number]) => {
	const locationState = checkSpaceLocationState({ currentSpace, currentConference, activeStatus });
	switch (locationState) {
		case "ONLINE":
			return _T("온라인");
		case "OFFLINE":
			return _T("오프라인");
		default:
			return locationState;
	}
};

export const useFileExtensions = () => {
	function getFileExtension(mimeType: string): Extension["type"] {
		let result: Extension["type"] = "etc";
		//@ts-ignore
		const patterns: Record<Extension["type"], string[]> = {
			pdf: ["application/pdf"],
			hwp: ["application/hwp", "application/hwp+zip", "application/octet-stream", "hwp", "hwpx"],
			word: ["msword", "wordprocessingml"],
			excel: ["ms-excel", "spreadsheetml"],
			ppt: ["ms-powerpoint", "presentationml"],
			photoshop: ["photoshop"],
			illustrator: ["indesign", "application/illustrator"],
			image: ["image/"],
			video: ["video/"],
			audio: ["audio/"],
			zip: ["zip", "x-compress", "x-gzip", "x-rar-compressed", "x-7z-compressed"],
			txt: ["text/plain"],
			code: ["text/x-c", "text/x-c++src", "java-source", "text/x-java-source"],
			md: ["md", "text/x-web-markdown"],
			html: ["html", "text/html"],
			json: ["json", "application/json"],
			csv: ["csv", "text/csv"],
			js: ["js", "text/javascript", "application/javascript"],
			py: ["py", "text/x-python-script", "text/x-python"],
			exe: ["application/x-dosexec"]
		};

		Object.keys(patterns).forEach((_pattern) => {
			const pattern = _pattern as Extension["type"];
			if (patterns[pattern].some((pattern) => mimeType.includes(pattern))) {
				result = pattern;
			}
		});

		// hwpx는 hwp+zip으로 mimeType이 오기 때문에 zip이 아닌 hwp로 지정
		if (mimeType === "application/hwp+zip") {
			result = "hwp";
		}

		//photoshop은 image/vnd.adobe.photoshop 으로 오기때문에 image가 아닌 photoshop으로 지정
		if (mimeType === "image/vnd.adobe.photoshop") {
			result = "photoshop";
		}

		return result;
	}

	function getDroppedFileMimeType(mimeType: string, fileName: string) {
		// ""로 들어올 경우 파일 이름으로 한번 더 체크
		if (mimeType === "") return fileName.split(".").pop() || "";
		return mimeType;
	}

	return { getFileExtension, getDroppedFileMimeType };
};

export const isNotSupportPreview = ({ mimeType }: { mimeType: string }) => {
	const { getFileExtension } = useFileExtensions();
	return (
		getFileExtension(mimeType) === "etc" || getFileExtension(mimeType) === "zip" || mimeType === "video/x-msvideo"
	);
};

export const MessageInValidateFilename = (name: string) => {
	if (!RegExp.FILE_NAME_REG.test(name)) {
		return _T("파일명에 특수문자가 포함되어 있습니다. 특수문자를 삭제하고 다시 시도해주세요.");
	}

	if (name.length > 200) {
		return _T("파일명이 200자를 초과했습니다. 파일명을 줄여서 다시 시도해주세요.");
	}

	return "";
};
